import { createSlice } from '@reduxjs/toolkit'

// =====================================================
// GRADE BANDS
// =====================================================

const GRADE_BANDS = {
  cbse: [
    { min: 91, grade: 'A1', description: 'Outstanding' },
    { min: 81, grade: 'A2', description: 'Excellent' },
    { min: 71, grade: 'B1', description: 'Very Good' },
    { min: 61, grade: 'B2', description: 'Good' },
    { min: 51, grade: 'C1', description: 'Fair' },
    { min: 41, grade: 'C2', description: 'Average' },
    { min: 33, grade: 'D', description: 'Below Average' },
    { min: 0, grade: 'E', description: 'Needs Improvement' }
  ],

  fsc: [
    { min: 80, grade: 'A1', description: '1st Division' },
    { min: 70, grade: 'A', description: '1st Division' },
    { min: 60, grade: 'B', description: '1st Division' },
    { min: 50, grade: 'C', description: '2nd Division' },
    { min: 40, grade: 'D', description: '2nd Division' },
    { min: 33, grade: 'E', description: '3rd Division' },
    { min: 0, grade: 'F', description: 'Fail' }
  ],

  olevel: [
    { min: 90, grade: 'A*', description: 'Outstanding' },
    { min: 80, grade: 'A', description: 'Excellent' },
    { min: 70, grade: 'B', description: 'Good' },
    { min: 60, grade: 'C', description: 'Satisfactory' },
    { min: 50, grade: 'D', description: 'Below Average' },
    { min: 40, grade: 'E', description: 'Weak' },
    { min: 0, grade: 'U', description: 'Ungraded' }
  ]
}

function getGradeInfo(percentage, boardType) {
  const bands =
    GRADE_BANDS[boardType] || GRADE_BANDS.fsc

  return (
    bands.find((band) => percentage >= band.min) ||
    bands[bands.length - 1]
  )
}

// =====================================================
// INITIAL STATE
// =====================================================

const initialState = {
  // ===================================================
  // GENERIC PERCENTAGE
  // ===================================================

  mode: 'whatIs',
  x: '',
  y: '',
  result: null,
  formula: '',

  // ===================================================
  // BASIC PERCENTAGE
  // ===================================================

  percValue: '',
  percOf: '',
  percResult: null,

  // ===================================================
  // PERCENTAGE INCREASE
  // ===================================================

  increaseMode: 'findPercent', // 'findPercent' | 'applyIncrease'
  increaseOriginal: '',
  increaseNew: '',
  increasePercent: '',
  increaseResult: null,

  // ===================================================
  // PERCENTAGE DECREASE
  // ===================================================

  decreaseMode: 'findPercent', // 'findPercent' | 'applyDecrease' | 'findOriginal'
  decreaseOriginal: '',
  decreaseNew: '',
  decreasePercent: '',
  decreaseResult: null,

  // ===================================================
  // MARKS PERCENTAGE
  // ===================================================

  marksMode: 'quick',
  marksObtained: '',
  marksTotal: '',

  boardType: 'fsc',

  marksResult: null,

  // Subject-wise marks
  subjects: [
    {
      id: '1',
      name: 'Subject 1',
      obtained: '',
      total: ''
    },
    {
      id: '2',
      name: 'Subject 2',
      obtained: '',
      total: ''
    }
  ],

  subjectWiseResult: null,

  // Target marks
  targetPercent: '',
  targetTotalMarks: '',
  targetResult: null,

  // ===================================================
  // DISCOUNT
  // ===================================================

  discountMode: 'percentOff',
  discountPrice: '',
  discountPercent: '',
  discountSalePrice: '',
  discountResult: null,

  // ===================================================
  // TAX
  // ===================================================

  taxAmount: '',
  taxRate: '',
  taxType: 'sales-tax',
  taxRegion: 'us-ca',
  taxResult: null,

  // ===================================================
  // TIP
  // ===================================================

  tipMode: 'percent', // 'percent' | 'custom'
  billAmount: '',
  tipPercent: '18',
  customTipAmount: '',
  tipTaxAmount: '',
  tipOnPostTax: false,
  roundUpTotal: false,
  billPeople: '1',
  tipResult: null,

  // ===================================================
  // COMPOUND INTEREST
  // ===================================================

  principal: '',
  rateOfInterest: '',
  years: '',
  compoundFrequency: 'yearly',
  compoundResult: null,

  // ===================================================
  // GPA / SGPA
  // ===================================================

  gpaMode: 'sgpa',

  courses: [
    {
      id: '1',
      name: 'Course 1',
      creditHours: '3',
      gradePoints: '4.0'
    },
    {
      id: '2',
      name: 'Course 2',
      creditHours: '3',
      gradePoints: '3.0'
    },
    {
      id: '3',
      name: 'Course 3',
      creditHours: '4',
      gradePoints: '3.5'
    }
  ],

  sgpaResult: null,

  // Kept for compatibility with components
  subjectWiseGpaResult: null,

  // ===================================================
  // CGPA
  // ===================================================

  semesters: [
    {
      id: '1',
      name: 'Semester 1',
      credits: '16',
      sgpa: '3.5'
    },
    {
      id: '2',
      name: 'Semester 2',
      credits: '18',
      sgpa: '3.2'
    }
  ],

  cgpaResult: null,

  // ===================================================
  // GPA ESTIMATOR
  // ===================================================

  estimatorCurrentCGPA: '',
  estimatorCurrentCredits: '',
  estimatorTargetCGPA: '',
  estimatorFutureCredits: '',
  estimatorResult: null,

  // ===================================================
  // GENERAL
  // ===================================================

  activeTab: 'basic',
  history: []
}

// =====================================================
// SLICE
// =====================================================

const percentageSlice = createSlice({
  name: 'percentage',

  initialState,

  reducers: {
    // =================================================
    // GENERAL
    // =================================================

    setActiveTab: (state, action) => {
      state.activeTab = action.payload
    },

    // =================================================
    // GENERIC PERCENTAGE
    // =================================================

    setMode: (state, action) => {
      state.mode = action.payload
      state.result = null
      state.formula = ''
    },

    setX: (state, action) => {
      state.x = action.payload
    },

    setY: (state, action) => {
      state.y = action.payload
    },

    calculate: (state) => {
      const numX = parseFloat(state.x)
      const numY = parseFloat(state.y)

      if (isNaN(numX) || isNaN(numY)) {
        state.result = null
        state.formula = ''
        return
      }

      switch (state.mode) {
        case 'whatIs': {
          const res = (numX / 100) * numY

          state.result = res

          state.formula =
            `(${numX} ÷ 100) × ${numY} = ${res}`

          break
        }

        case 'whatPercent': {
          if (numY === 0) {
            state.result = null
            state.formula = 'Cannot divide by zero'
            return
          }

          const res = (numX / numY) * 100

          state.result = res

          state.formula =
            `(${numX} ÷ ${numY}) × 100 = ${res}%`

          break
        }

        case 'percentChange': {
          if (numX === 0) {
            state.result = null
            state.formula =
              'Initial value cannot be zero'
            return
          }

          const res =
            ((numY - numX) / numX) * 100

          state.result = res

          state.formula =
            `((${numY} - ${numX}) ÷ ${numX}) × 100 = ${res}%`

          break
        }

        default:
          state.result = null
          state.formula = ''
      }
    },

    clearResult: (state) => {
      state.x = ''
      state.y = ''
      state.result = null
      state.formula = ''
    },

    // =================================================
    // BASIC PERCENTAGE
    // =================================================

    setPercValue: (state, action) => {
      state.percValue = action.payload
    },

    setPercOf: (state, action) => {
      state.percOf = action.payload
    },

    calculateBasicPerc: (state) => {
      const value =
        parseFloat(state.percValue)

      const ofValue =
        parseFloat(state.percOf)

      if (
        !isNaN(value) &&
        !isNaN(ofValue)
      ) {
        state.percResult =
          ((value / 100) * ofValue).toFixed(2)
      } else {
        state.percResult = null
      }
    },

    clearBasicPerc: (state) => {
      state.percValue = ''
      state.percOf = ''
      state.percResult = null
    },

    // =================================================
    // PERCENTAGE INCREASE
    // =================================================

    setIncreaseMode: (state, action) => {
      state.increaseMode = action.payload
      state.increaseResult = null
    },

    setIncreaseOriginal: (state, action) => {
      state.increaseOriginal = action.payload
    },

    setIncreaseNew: (state, action) => {
      state.increaseNew = action.payload
    },

    setIncreasePercent: (state, action) => {
      state.increasePercent = action.payload
    },

    calculateIncrease: (state) => {
      const mode = state.increaseMode || 'findPercent'

      // ---------------------------------------------
      // Find the percentage increase between two values
      // ---------------------------------------------
      if (mode === 'findPercent') {
        const original = parseFloat(state.increaseOriginal)
        const newValue = parseFloat(state.increaseNew)

        if (!isNaN(original) && !isNaN(newValue) && original !== 0) {
          const difference = newValue - original
          const percentage = (difference / original) * 100

          state.increaseResult = {
            mode,
            percentage: percentage.toFixed(2),
            difference: difference.toFixed(2),
            formula: `((${newValue} \u2212 ${original}) \u00f7 ${original}) \u00d7 100 = ${percentage.toFixed(2)}%`
          }
        } else {
          state.increaseResult = null
        }
        return
      }

      // ---------------------------------------------
      // Add a known percentage to a starting value
      // ---------------------------------------------
      if (mode === 'applyIncrease') {
        const original = parseFloat(state.increaseOriginal)
        const percent = parseFloat(state.increasePercent)

        if (!isNaN(original) && !isNaN(percent)) {
          const difference = (original * percent) / 100
          const newValue = original + difference

          state.increaseResult = {
            mode,
            newValue: newValue.toFixed(2),
            difference: difference.toFixed(2),
            formula: `${original} + (${original} \u00d7 ${percent}%) = ${newValue.toFixed(2)}`
          }
        } else {
          state.increaseResult = null
        }
        return
      }

      state.increaseResult = null
    },

    clearIncrease: (state) => {
      state.increaseOriginal = ''
      state.increaseNew = ''
      state.increasePercent = ''
      state.increaseResult = null
    },

    // =================================================
    // PERCENTAGE DECREASE
    // =================================================

    setDecreaseMode: (state, action) => {
      state.decreaseMode = action.payload
      state.decreaseResult = null
    },

    setDecreaseOriginal: (state, action) => {
      state.decreaseOriginal = action.payload
    },

    setDecreaseNew: (state, action) => {
      state.decreaseNew = action.payload
    },

    setDecreasePercent: (state, action) => {
      state.decreasePercent = action.payload
    },

    calculateDecrease: (state) => {
      const mode = state.decreaseMode || 'findPercent'

      // ---------------------------------------------
      // Find the percentage decrease between two values
      // ---------------------------------------------
      if (mode === 'findPercent') {
        const original = parseFloat(state.decreaseOriginal)
        const newValue = parseFloat(state.decreaseNew)

        if (!isNaN(original) && !isNaN(newValue) && original !== 0) {
          const difference = original - newValue
          const percentage = (difference / original) * 100

          state.decreaseResult = {
            mode,
            percentage: percentage.toFixed(2),
            difference: difference.toFixed(2),
            formula: `((${original} \u2212 ${newValue}) \u00f7 ${original}) \u00d7 100 = ${percentage.toFixed(2)}%`
          }
        } else {
          state.decreaseResult = null
        }
        return
      }

      // ---------------------------------------------
      // Apply a known percentage decrease to a value
      // ---------------------------------------------
      if (mode === 'applyDecrease') {
        const original = parseFloat(state.decreaseOriginal)
        const percent = parseFloat(state.decreasePercent)

        if (!isNaN(original) && !isNaN(percent)) {
          const difference = (original * percent) / 100
          const newValue = original - difference

          state.decreaseResult = {
            mode,
            newValue: newValue.toFixed(2),
            difference: difference.toFixed(2),
            formula: `${original} \u2212 (${original} \u00d7 ${percent}%) = ${newValue.toFixed(2)}`
          }
        } else {
          state.decreaseResult = null
        }
        return
      }

      // ---------------------------------------------
      // Find the original value from a reduced value + percentage
      // (the "percentage decrease without original number" case)
      // ---------------------------------------------
      if (mode === 'findOriginal') {
        const newValue = parseFloat(state.decreaseNew)
        const percent = parseFloat(state.decreasePercent)

        if (!isNaN(newValue) && !isNaN(percent) && percent >= 0 && percent < 100) {
          const originalValue = newValue / (1 - percent / 100)
          const difference = originalValue - newValue

          state.decreaseResult = {
            mode,
            originalValue: originalValue.toFixed(2),
            difference: difference.toFixed(2),
            formula: `${newValue} \u00f7 (1 \u2212 ${percent}/100) = ${originalValue.toFixed(2)}`
          }
        } else {
          state.decreaseResult = null
        }
        return
      }

      state.decreaseResult = null
    },

    clearDecrease: (state) => {
      state.decreaseOriginal = ''
      state.decreaseNew = ''
      state.decreasePercent = ''
      state.decreaseResult = null
    },

    // =================================================
    // MARKS PERCENTAGE
    // =================================================

    setMarksMode: (state, action) => {
      state.marksMode = action.payload
    },

    setMarksObtained: (state, action) => {
      state.marksObtained = action.payload
    },

    setMarksTotal: (state, action) => {
      state.marksTotal = action.payload
    },

    setBoardType: (state, action) => {
      state.boardType = action.payload
    },

    // -----------------------------------------------
    // Quick Marks Calculation
    // -----------------------------------------------

    calculateMarks: (state) => {
      const obtained =
        parseFloat(state.marksObtained)

      const total =
        parseFloat(state.marksTotal)

      if (
        !isNaN(obtained) &&
        !isNaN(total) &&
        total > 0
      ) {
        state.marksResult =
          ((obtained / total) * 100).toFixed(2)
      } else {
        state.marksResult = null
      }
    },

    // -----------------------------------------------
    // Marks Percentage + Grade
    // -----------------------------------------------

    calculateMarksPercent: (state) => {
      const obtained =
        parseFloat(state.marksObtained)

      const total =
        parseFloat(state.marksTotal)

      if (
        !isNaN(obtained) &&
        !isNaN(total) &&
        total > 0
      ) {
        const percentage =
          (obtained / total) * 100

        const rounded =
          percentage.toFixed(2)

        const gradeInfo =
          getGradeInfo(
            percentage,
            state.boardType
          )

        state.marksResult = {
          percentage: rounded,
          grade: gradeInfo.grade,
          description:
            gradeInfo.description,
          formula:
            `(${obtained} ÷ ${total}) × 100 = ${rounded}%`
        }
      } else {
        state.marksResult = null
      }
    },

    clearMarks: (state) => {
      state.marksObtained = ''
      state.marksTotal = ''
      state.marksResult = null
    },

    clearMarksPercent: (state) => {
      state.marksObtained = ''
      state.marksTotal = ''
      state.marksResult = null
    },

    // =================================================
    // SUBJECT-WISE MARKS
    // =================================================

    addSubjectItem: (state) => {
      const nextId = String(Date.now())

      state.subjects.push({
        id: nextId,
        name:
          `Subject ${state.subjects.length + 1}`,
        obtained: '',
        total: ''
      })
    },

    // Compatibility alias
    addSubject: (state) => {
      const nextId = String(Date.now())

      state.subjects.push({
        id: nextId,
        name:
          `Subject ${state.subjects.length + 1}`,
        obtained: '',
        total: ''
      })
    },

    removeSubjectItem: (state, action) => {
      if (state.subjects.length > 1) {
        state.subjects =
          state.subjects.filter(
            subject =>
              subject.id !== action.payload
          )
      }
    },

    // Compatibility alias
    removeSubject: (state, action) => {
      if (state.subjects.length > 1) {
        state.subjects =
          state.subjects.filter(
            subject =>
              subject.id !== action.payload
          )
      }
    },

    updateSubjectItem: (state, action) => {
      const {
        id,
        field,
        value
      } = action.payload

      const subject =
        state.subjects.find(
          item => item.id === id
        )

      if (subject) {
        subject[field] = value
      }
    },

    // Compatibility alias
    updateSubject: (state, action) => {
      const {
        id,
        field,
        value
      } = action.payload

      const subject =
        state.subjects.find(
          item => item.id === id
        )

      if (subject) {
        subject[field] = value
      }
    },

    // -----------------------------------------------
    // Subject-Wise Calculation
    // -----------------------------------------------

    calculateSubjectWise: (state) => {
      let totalObtained = 0
      let totalMarks = 0
      let valid = false

      const breakdown = []

      state.subjects.forEach(
        (subject, index) => {
          const obtained =
            parseFloat(subject.obtained)

          const total =
            parseFloat(subject.total)

          if (
            !isNaN(obtained) &&
            !isNaN(total) &&
            total > 0
          ) {
            valid = true

            totalObtained += obtained
            totalMarks += total

            breakdown.push({
              name:
                subject.name ||
                `Subject ${index + 1}`,

              obtained,

              total,

              percent:
                (
                  (obtained / total) *
                  100
                ).toFixed(2)
            })
          }
        }
      )

      if (
        valid &&
        totalMarks > 0
      ) {
        const percentage =
          (totalObtained / totalMarks) * 100

        const rounded =
          percentage.toFixed(2)

        const gradeInfo =
          getGradeInfo(
            percentage,
            state.boardType
          )

        state.subjectWiseResult = {
          percentage: rounded,

          grade: gradeInfo.grade,

          description:
            gradeInfo.description,

          formula:
            `${totalObtained} Total Obtained ÷ ${totalMarks} Total Marks × 100 = ${rounded}%`,

          breakdown
        }
      } else {
        state.subjectWiseResult = null
      }
    },

    clearSubjectWise: (state) => {
      state.subjects = [
        {
          id: '1',
          name: 'Subject 1',
          obtained: '',
          total: ''
        },
        {
          id: '2',
          name: 'Subject 2',
          obtained: '',
          total: ''
        }
      ]

      state.subjectWiseResult = null
    },

    // =================================================
    // TARGET MARKS
    // =================================================

    setTargetPercent: (state, action) => {
      state.targetPercent = action.payload
    },

    setTargetTotalMarks: (state, action) => {
      state.targetTotalMarks = action.payload
    },

    calculateTargetMarks: (state) => {
      const percentage =
        parseFloat(state.targetPercent)

      const total =
        parseFloat(state.targetTotalMarks)

      if (
        !isNaN(percentage) &&
        !isNaN(total) &&
        total > 0
      ) {
        const needed =
          Math.ceil(
            (percentage / 100) * total
          )

        state.targetResult = {
          neededMarks: needed,

          formula:
            `(${percentage} ÷ 100) × ${total} = ${needed} marks (rounded up)`
        }
      } else {
        state.targetResult = null
      }
    },

    clearTargetMarks: (state) => {
      state.targetPercent = ''
      state.targetTotalMarks = ''
      state.targetResult = null
    },

    // =================================================
    // DISCOUNT
    // =================================================

    setDiscountMode: (state, action) => {
      state.discountMode = action.payload
      state.discountResult = null
    },

    setDiscountPrice: (state, action) => {
      state.discountPrice = action.payload
    },

    setDiscountPercent: (state, action) => {
      state.discountPercent = action.payload
    },

    setDiscountSalePrice: (state, action) => {
      state.discountSalePrice = action.payload
    },

    calculateDiscount: (state) => {
      const mode =
        state.discountMode || 'percentOff'

      // ---------------------------------------------
      // Percentage off
      // ---------------------------------------------

      if (mode === 'percentOff') {
        const price =
          parseFloat(state.discountPrice)

        const discount =
          parseFloat(state.discountPercent)

        if (
          !isNaN(price) &&
          !isNaN(discount)
        ) {
          const discountAmount =
            (price * discount) / 100

          const finalPrice =
            price - discountAmount

          state.discountResult = {
            mode,

            finalPrice:
              finalPrice.toFixed(2),

            discountAmount:
              discountAmount.toFixed(2),

            youPay:
              (100 - discount).toFixed(2),

            formula:
              `$${price} × ${discount}% = $${discountAmount.toFixed(2)} saved → Final Price: $${finalPrice.toFixed(2)}`
          }
        } else {
          state.discountResult = null
        }

        return
      }

      // ---------------------------------------------
      // Find original price
      // ---------------------------------------------

      if (mode === 'findOriginal') {
        const salePrice =
          parseFloat(
            state.discountSalePrice
          )

        const discount =
          parseFloat(
            state.discountPercent
          )

        if (
          !isNaN(salePrice) &&
          !isNaN(discount) &&
          discount >= 0 &&
          discount < 100
        ) {
          const originalPrice =
            salePrice /
            (1 - discount / 100)

          const discountAmount =
            originalPrice - salePrice

          state.discountResult = {
            mode,

            originalPrice:
              originalPrice.toFixed(2),

            discountAmount:
              discountAmount.toFixed(2),

            formula:
              `$${salePrice} ÷ (1 − ${discount}/100) = $${originalPrice.toFixed(2)}`
          }
        } else {
          state.discountResult = null
        }

        return
      }

      // ---------------------------------------------
      // Find discount percentage
      // ---------------------------------------------

      if (mode === 'findPercent') {
        const originalPrice =
          parseFloat(
            state.discountPrice
          )

        const salePrice =
          parseFloat(
            state.discountSalePrice
          )

        if (
          !isNaN(originalPrice) &&
          !isNaN(salePrice) &&
          originalPrice > 0
        ) {
          const discountAmount =
            originalPrice - salePrice

          const discountPercent =
            (discountAmount /
              originalPrice) *
            100

          state.discountResult = {
            mode,

            discountPercentFound:
              discountPercent.toFixed(2),

            discountAmount:
              discountAmount.toFixed(2),

            formula:
              `(($${originalPrice} − $${salePrice}) ÷ $${originalPrice}) × 100 = ${discountPercent.toFixed(2)}%`
          }
        } else {
          state.discountResult = null
        }

        return
      }

      state.discountResult = null
    },

    clearDiscount: (state) => {
      state.discountPrice = ''
      state.discountPercent = ''
      state.discountSalePrice = ''
      state.discountResult = null
    },

    // =================================================
    // TAX
    // =================================================

    setTaxAmount: (state, action) => {
      state.taxAmount = action.payload
    },

    setTaxRate: (state, action) => {
      state.taxRate = action.payload
    },

    setTaxType: (state, action) => {
      state.taxType = action.payload
    },

    setTaxRegion: (state, action) => {
      state.taxRegion = action.payload
    },

    calculateTax: (state) => {
      const amount =
        parseFloat(state.taxAmount)

      const rate =
        parseFloat(state.taxRate)

      if (
        !isNaN(amount) &&
        !isNaN(rate)
      ) {
        const tax =
          (amount * rate) / 100

        const total =
          amount + tax

        state.taxResult = {
          taxAmount: tax.toFixed(2),
          totalAmount: total.toFixed(2),
          formula: `Tax = $${amount} × (${rate}% ÷ 100) = $${tax.toFixed(2)}`
        }
      } else {
        state.taxResult = null
      }
    },
    clearTax: (state) => {
      state.taxAmount = ''
      state.taxRate = ''
      state.taxResult = null
    },

    // =================================================
    // TIP
    // =================================================

    setTipMode: (state, action) => {
      state.tipMode = action.payload
      state.tipResult = null
    },

    setBillAmount: (state, action) => {
      state.billAmount = action.payload
    },

    setTipPercent: (state, action) => {
      state.tipPercent = action.payload
    },

    setCustomTipAmount: (state, action) => {
      state.customTipAmount = action.payload
    },

    setTipTaxAmount: (state, action) => {
      state.tipTaxAmount = action.payload
    },

    setTipOnPostTax: (state, action) => {
      state.tipOnPostTax = action.payload
    },

    setRoundUpTotal: (state, action) => {
      state.roundUpTotal = action.payload
    },

    setBillPeople: (state, action) => {
      state.billPeople = action.payload
    },

    calculateTip: (state) => {
      const bill = parseFloat(state.billAmount)
      const tax = parseFloat(state.tipTaxAmount) || 0
      const people = parseInt(state.billPeople, 10) || 1

      if (isNaN(bill)) {
        state.tipResult = null
        return
      }

      const tipBase = state.tipOnPostTax ? bill + tax : bill

      let tipAmount
      let formula

      if (state.tipMode === 'custom') {
        const customAmount = parseFloat(state.customTipAmount)
        if (isNaN(customAmount)) {
          state.tipResult = null
          return
        }
        tipAmount = customAmount
        formula = `Custom tip: $${customAmount.toFixed(2)}`
      } else {
        const tipPct = parseFloat(state.tipPercent)
        if (isNaN(tipPct)) {
          state.tipResult = null
          return
        }
        tipAmount = (tipBase * tipPct) / 100
        formula = state.tipOnPostTax
          ? `($${bill} + $${tax} tax) \u00d7 ${tipPct}% = $${tipAmount.toFixed(2)} tip`
          : `$${bill} \u00d7 ${tipPct}% = $${tipAmount.toFixed(2)} tip`
      }

      const grandTotal = bill + tax + tipAmount
      const perPersonExact = grandTotal / people
      const perPerson = state.roundUpTotal ? Math.ceil(perPersonExact) : perPersonExact

      state.tipResult = {
        tipAmount: tipAmount.toFixed(2),
        totalBill: grandTotal.toFixed(2),
        perPerson: perPerson.toFixed(2),
        rounded: state.roundUpTotal,
        formula
      }
    },

    clearTip: (state) => {
      state.billAmount = ''
      state.tipPercent = '18'
      state.customTipAmount = ''
      state.tipTaxAmount = ''
      state.tipOnPostTax = false
      state.roundUpTotal = false
      state.billPeople = '1'
      state.tipResult = null
    },

    // =================================================
    // COMPOUND INTEREST
    // =================================================

    setPrincipal: (state, action) => {
      state.principal = action.payload
    },

    setRateOfInterest: (state, action) => {
      state.rateOfInterest = action.payload
    },

    setYears: (state, action) => {
      state.years = action.payload
    },

    setCompoundFrequency: (state, action) => {
      state.compoundFrequency =
        action.payload
    },

    calculateCompoundInterest: (state) => {
      const principal =
        parseFloat(state.principal)

      const rate =
        parseFloat(
          state.rateOfInterest
        ) / 100

      const years =
        parseFloat(state.years)

      let frequency = 1

      switch (
      state.compoundFrequency
      ) {
        case 'monthly':
          frequency = 12
          break

        case 'quarterly':
          frequency = 4
          break

        case 'semi-annually':
          frequency = 2
          break

        case 'yearly':
        default:
          frequency = 1
          break
      }

      if (
        !isNaN(principal) &&
        !isNaN(rate) &&
        !isNaN(years)
      ) {
        const amount =
          principal *
          Math.pow(
            1 + rate / frequency,
            frequency * years
          )

        const interest =
          amount - principal

        state.compoundResult = {
          interest:
            interest.toFixed(2),

          total:
            amount.toFixed(2)
        }
      } else {
        state.compoundResult = null
      }
    },

    clearCompoundInterest: (state) => {
      state.principal = ''
      state.rateOfInterest = ''
      state.years = ''
      state.compoundResult = null
    },

    // =================================================
    // GPA / SGPA
    // =================================================

    setGpaMode: (state, action) => {
      state.gpaMode = action.payload
    },

    addCourseItem: (state) => {
      const nextId =
        String(Date.now())

      state.courses.push({
        id: nextId,

        name:
          `Course ${state.courses.length + 1}`,

        creditHours: '3',

        gradePoints: '4.0'
      })
    },

    // Alias for components that call it Subject
    addGpaSubject: (state) => {
      const nextId =
        String(Date.now())

      state.courses.push({
        id: nextId,

        name:
          `Course ${state.courses.length + 1}`,

        creditHours: '3',

        gradePoints: '4.0'
      })
    },

    removeCourseItem: (state, action) => {
      if (
        state.courses.length > 1
      ) {
        state.courses =
          state.courses.filter(
            course =>
              course.id !== action.payload
          )
      }
    },

    updateCourseItem: (state, action) => {
      const {
        id,
        field,
        value
      } = action.payload

      const course =
        state.courses.find(
          item => item.id === id
        )

      if (course) {
        course[field] = value
      }
    },

    calculateSGPA: (state) => {
      let totalQualityPoints = 0
      let totalCreditHours = 0

      state.courses.forEach(
        course => {
          const creditHours =
            parseFloat(
              course.creditHours
            ) || 0

          const gradePoints =
            parseFloat(
              course.gradePoints
            ) || 0

          totalQualityPoints +=
            creditHours *
            gradePoints

          totalCreditHours +=
            creditHours
        }
      )

      if (
        totalCreditHours > 0
      ) {
        const sgpa =
          (
            totalQualityPoints /
            totalCreditHours
          ).toFixed(2)

        state.sgpaResult = {
          sgpa,

          totalCreditHours,

          totalQualityPoints:
            totalQualityPoints.toFixed(2),

          formula:
            `SGPA = ${totalQualityPoints.toFixed(2)} Total Quality Points ÷ ${totalCreditHours} Total Credits`
        }
      } else {
        state.sgpaResult = null
      }
    },

    // Compatibility action
    calculateSubjectWiseGPA: (state) => {
      const breakdown =
        state.courses.map(
          course => {
            const creditHours =
              parseFloat(
                course.creditHours
              ) || 0

            const gradePoints =
              parseFloat(
                course.gradePoints
              ) || 0

            return {
              id: course.id,

              name: course.name,

              creditHours,

              gradePoints,

              qualityPoints:
                (
                  creditHours *
                  gradePoints
                ).toFixed(2)
            }
          }
        )

      state.subjectWiseGpaResult =
        breakdown
    },

    clearSGPA: (state) => {
      state.courses = [
        {
          id: '1',
          name: 'Course 1',
          creditHours: '3',
          gradePoints: '4.0'
        },
        {
          id: '2',
          name: 'Course 2',
          creditHours: '3',
          gradePoints: '3.0'
        }
      ]

      state.sgpaResult = null
      state.subjectWiseGpaResult = null
    },

    // =================================================
    // CGPA
    // =================================================

    addSemesterItem: (state) => {
      const nextId =
        String(Date.now())

      state.semesters.push({
        id: nextId,

        name:
          `Semester ${state.semesters.length + 1}`,

        credits: '16',

        sgpa: '3.5'
      })
    },

    removeSemesterItem: (state, action) => {
      if (
        state.semesters.length > 1
      ) {
        state.semesters =
          state.semesters.filter(
            semester =>
              semester.id !==
              action.payload
          )
      }
    },

    updateSemesterItem: (
      state,
      action
    ) => {
      const {
        id,
        field,
        value
      } = action.payload

      const semester =
        state.semesters.find(
          item => item.id === id
        )

      if (semester) {
        semester[field] = value
      }
    },

    calculateCGPA: (state) => {
      let totalQualityPoints = 0
      let totalCredits = 0

      state.semesters.forEach(
        semester => {
          const credits =
            parseFloat(
              semester.credits
            ) || 0

          const sgpa =
            parseFloat(
              semester.sgpa
            ) || 0

          totalQualityPoints +=
            credits * sgpa

          totalCredits +=
            credits
        }
      )

      if (
        totalCredits > 0
      ) {
        const cgpa =
          (
            totalQualityPoints /
            totalCredits
          ).toFixed(2)

        state.cgpaResult = {
          cgpa,

          totalCredits,

          totalQualityPoints:
            totalQualityPoints.toFixed(2),

          formula:
            `CGPA = ${totalQualityPoints.toFixed(2)} Sum Points ÷ ${totalCredits} Cumulative Credits`
        }
      } else {
        state.cgpaResult = null
      }
    },

    clearCGPA: (state) => {
      state.semesters = [
        {
          id: '1',
          name: 'Semester 1',
          credits: '16',
          sgpa: '3.5'
        }
      ]

      state.cgpaResult = null
    },

    // =================================================
    // GPA ESTIMATOR
    // =================================================

    setEstimatorCurrentCGPA: (
      state,
      action
    ) => {
      state.estimatorCurrentCGPA =
        action.payload
    },

    setEstimatorCurrentCredits: (
      state,
      action
    ) => {
      state.estimatorCurrentCredits =
        action.payload
    },

    setEstimatorTargetCGPA: (
      state,
      action
    ) => {
      state.estimatorTargetCGPA =
        action.payload
    },

    setEstimatorFutureCredits: (
      state,
      action
    ) => {
      state.estimatorFutureCredits =
        action.payload
    },

    calculateEstimator: (state) => {
      const currentCGPA =
        parseFloat(
          state.estimatorCurrentCGPA
        )

      const currentCredits =
        parseFloat(
          state.estimatorCurrentCredits
        )

      const targetCGPA =
        parseFloat(
          state.estimatorTargetCGPA
        )

      const futureCredits =
        parseFloat(
          state.estimatorFutureCredits
        )

      if (
        isNaN(currentCGPA) ||
        isNaN(currentCredits) ||
        isNaN(targetCGPA) ||
        isNaN(futureCredits) ||
        futureCredits <= 0 ||
        currentCredits < 0
      ) {
        state.estimatorResult = null
        return
      }

      const currentPoints =
        currentCGPA *
        currentCredits

      const totalCredits =
        currentCredits +
        futureCredits

      const targetPoints =
        targetCGPA *
        totalCredits

      const neededPoints =
        targetPoints -
        currentPoints

      const requiredSGPA =
        neededPoints /
        futureCredits

      state.estimatorResult = {
        requiredSGPA:
          requiredSGPA.toFixed(2),

        achievable:
          requiredSGPA <= 4.0,

        alreadyMet:
          requiredSGPA <= 0,

        formula:
          `Required SGPA = (${targetCGPA} × ${totalCredits} − ${currentCGPA} × ${currentCredits}) ÷ ${futureCredits}`
      }
    },

    clearEstimator: (state) => {
      state.estimatorCurrentCGPA = ''
      state.estimatorCurrentCredits = ''
      state.estimatorTargetCGPA = ''
      state.estimatorFutureCredits = ''
      state.estimatorResult = null
    },

    // =================================================
    // HISTORY
    // =================================================

    clearHistory: (state) => {
      state.history = []
    }
  }
})

// =====================================================
// EXPORT ALL ACTIONS
// =====================================================

export const {
  // General
  setActiveTab,

  // Generic Percentage
  setMode,
  setX,
  setY,
  calculate,
  clearResult,

  // Basic Percentage
  setPercValue,
  setPercOf,
  calculateBasicPerc,
  clearBasicPerc,

  // Percentage Increase
  setIncreaseMode,
  setIncreaseOriginal,
  setIncreaseNew,
  setIncreasePercent,
  calculateIncrease,
  clearIncrease,

  // Percentage Decrease
  setDecreaseMode,
  setDecreaseOriginal,
  setDecreaseNew,
  setDecreasePercent,
  calculateDecrease,
  clearDecrease,

  // Marks Percentage
  setMarksMode,
  setMarksObtained,
  setMarksTotal,
  setBoardType,
  calculateMarks,
  calculateMarksPercent,
  clearMarks,
  clearMarksPercent,

  // Subject-Wise Marks
  addSubjectItem,
  addSubject,
  removeSubjectItem,
  removeSubject,
  updateSubjectItem,
  updateSubject,
  calculateSubjectWise,
  clearSubjectWise,

  // Target Marks
  setTargetPercent,
  setTargetTotalMarks,
  calculateTargetMarks,
  clearTargetMarks,

  // Discount
  setDiscountMode,
  setDiscountPrice,
  setDiscountPercent,
  setDiscountSalePrice,
  calculateDiscount,
  clearDiscount,

  // Tax
  setTaxAmount,
  setTaxRate,
  setTaxType,
  setTaxRegion,
  calculateTax,
  clearTax,

  // Tip
  setTipMode,
  setBillAmount,
  setTipPercent,
  setCustomTipAmount,
  setTipTaxAmount,
  setTipOnPostTax,
  setRoundUpTotal,
  setBillPeople,
  calculateTip,
  clearTip,

  // Compound Interest
  setPrincipal,
  setRateOfInterest,
  setYears,
  setCompoundFrequency,
  calculateCompoundInterest,
  clearCompoundInterest,

  // GPA / SGPA
  setGpaMode,
  addCourseItem,
  addGpaSubject,
  removeCourseItem,
  updateCourseItem,
  calculateSGPA,
  calculateSubjectWiseGPA,
  clearSGPA,

  // CGPA
  addSemesterItem,
  removeSemesterItem,
  updateSemesterItem,
  calculateCGPA,
  clearCGPA,

  // GPA Estimator
  setEstimatorCurrentCGPA,
  setEstimatorCurrentCredits,
  setEstimatorTargetCGPA,
  setEstimatorFutureCredits,
  calculateEstimator,
  clearEstimator,

  // History
  clearHistory
} = percentageSlice.actions

// =====================================================
// DEFAULT REDUCER
// =====================================================

export default percentageSlice.reducer